import { combineReducers } from '@fullstory/redux-util/utils';
import { alertsReducer } from './modules/alerts';
import { NAME as ANNOTATIONS_NAME, annotationsReducer } from './modules/annotations';
import { appBackendReducer } from './modules/appbackend';
import { classificationRulesReducer } from './modules/classificationRules';
import { dashboardReducer } from './modules/dashboards';
import { datalossReducer } from './modules/dataloss/reducers';
import { dxObjectsCacheReducer } from './modules/dxObjectsCache/reducers';
import { elementPropertyRulesReducer } from './modules/elementPropertyRules';
import { fullstoryReducer } from './modules/fullstory';
import { funnelReducer } from './modules/funnels';
import { gravatarsReducer } from './modules/gravatars';
import { heatmapsReducer } from './modules/heatmaps/reducers';
import { helpOverlayReducer } from './modules/helpOverlay/reducers';
import { highlightsReducer } from './modules/highlights';
import { homeReducer } from './modules/home';
import { impactReducer } from './modules/impacts';
import { ingestionSettingsReducer } from './modules/ingestionSettings';
import { issueTrackingReducer } from './modules/issuetracking';
import { journeyMapsReducer } from './modules/journeys/reducers';
import { libraryReducer } from './modules/library';
import { assistantReducer, llmToolReducer } from './modules/llm-tool/reducers';
import { mapsReducer } from './modules/maps';
import { matchingUsersReducer } from './modules/matchingUsers';
import { metricsReducer } from './modules/metrics';
import { metricsInsightsReducer } from './modules/metricsInsights';
import { namedElementBlockRuleReducer } from './modules/namedElementBlockRules';
import { namedElementsReducer } from './modules/namedElements';
import { notificationsReducer } from './modules/notifications/reducers';
import { unnamedOverlayReducer } from './modules/overlays/reducers';
import { pagesReducer } from './modules/pages/reducers';
import { playbackReducer } from './modules/playback';
import { propertiesReducer } from './modules/properties/reducers';
import { retentionMetricsReducer } from './modules/retention';
import { searchReducer } from './modules/search';
import { searchEventsReducer } from './modules/searchEvents/reducers';
import { searchExportsReducer } from './modules/searchexports';
import { searchieReducer } from './modules/searchies';
import { searchMetaReducer } from './modules/searchMeta';
import { segmentReducer } from './modules/segments';
import {
  sessionPlaylistCardFilterReducer,
  sessionReplay as sessionReplayReducer,
} from './modules/sessionReplay/reducers';
import { settingsReducer } from './modules/settings';
import { spacesReducer } from './modules/spaces/reducers';
import { specsReducer } from './modules/specs/reducers';
import { unnamedSegmentReducer } from './modules/unnamedSegments';
import { userOverlayReducer } from './modules/userOverlay/reducers';
import { watchedElementsReducer } from './modules/watchedElements';
import { watchHistoryReducer } from './modules/watchHistory';
import { webhooksReducer } from './modules/webhooks';

export type RootReducerState = {
  annotations: ReturnType<typeof annotationsReducer>;
  fullstory: ReturnType<typeof fullstoryReducer>;
  impacts: ReturnType<typeof impactReducer>;
  segments: ReturnType<typeof segmentReducer>;
  unnamedSegments: ReturnType<typeof unnamedSegmentReducer>;
  funnels: ReturnType<typeof funnelReducer>;
  gravatars: ReturnType<typeof gravatarsReducer>;
  matchingUsers: ReturnType<typeof matchingUsersReducer>;
  searchies: ReturnType<typeof searchieReducer>;
  settings: ReturnType<typeof settingsReducer>;
  webhooks: ReturnType<typeof webhooksReducer>;
  alerts: ReturnType<typeof alertsReducer>;
  playback: ReturnType<typeof playbackReducer>;
  dataloss: ReturnType<typeof datalossReducer>;
  dashboards: ReturnType<typeof dashboardReducer>;
  unnamedOverlays: ReturnType<typeof unnamedOverlayReducer>;
  searchExports: ReturnType<typeof searchExportsReducer>;
  highlights: ReturnType<typeof highlightsReducer>;
  search: ReturnType<typeof searchReducer>;
  searchMeta: ReturnType<typeof searchMetaReducer>;
  maps: ReturnType<typeof mapsReducer>;
  metrics: ReturnType<typeof metricsReducer>;
  watchedElements: ReturnType<typeof watchedElementsReducer>;
  ingestionSettings: ReturnType<typeof ingestionSettingsReducer>;
  namedElements: ReturnType<typeof namedElementsReducer>;
  classificationRules: ReturnType<typeof classificationRulesReducer>;
  namedElementBlockRules: ReturnType<typeof namedElementBlockRuleReducer>;
  productTeams: ReturnType<typeof issueTrackingReducer>;
  watchHistory: ReturnType<typeof watchHistoryReducer>;
  helpOverlay: ReturnType<typeof helpOverlayReducer>;
  userOverlay: ReturnType<typeof userOverlayReducer>;
  pages: ReturnType<typeof pagesReducer>;
  journeyMaps: ReturnType<typeof journeyMapsReducer>;
  retentionMetrics: ReturnType<typeof retentionMetricsReducer>;
  heatmaps: ReturnType<typeof heatmapsReducer>;
  home: ReturnType<typeof homeReducer>;
  library: ReturnType<typeof libraryReducer>;
  sessionReplay: ReturnType<typeof sessionReplayReducer>;
  sesionReplayFilters: ReturnType<typeof sessionPlaylistCardFilterReducer>;
  metricsInsights: ReturnType<typeof metricsInsightsReducer>;
  appBackend: ReturnType<typeof appBackendReducer>;
  notifications: ReturnType<typeof notificationsReducer>;
  spaces: ReturnType<typeof spacesReducer>;
  assistant: ReturnType<typeof assistantReducer>;
  specs: ReturnType<typeof specsReducer>;
  dxObjectsCache: ReturnType<typeof dxObjectsCacheReducer>;
  elementPropertyRules: ReturnType<typeof elementPropertyRulesReducer>;
  llmTool: ReturnType<typeof llmToolReducer>;
  capturedEvents: ReturnType<typeof searchEventsReducer>;
  properties: ReturnType<typeof propertiesReducer>;
};

export const rootReducer = combineReducers<RootReducerState>({
  [ANNOTATIONS_NAME]: annotationsReducer,
  fullstory: fullstoryReducer,
  impacts: impactReducer,
  segments: segmentReducer,
  unnamedSegments: unnamedSegmentReducer,
  funnels: funnelReducer,
  gravatars: gravatarsReducer,
  matchingUsers: matchingUsersReducer,
  searchies: searchieReducer,
  settings: settingsReducer,
  webhooks: webhooksReducer,
  alerts: alertsReducer,
  playback: playbackReducer,
  dataloss: datalossReducer,
  dashboards: dashboardReducer,
  unnamedOverlays: unnamedOverlayReducer,
  searchExports: searchExportsReducer,
  highlights: highlightsReducer,
  search: searchReducer,
  searchMeta: searchMetaReducer,
  maps: mapsReducer,
  metrics: metricsReducer,
  watchedElements: watchedElementsReducer,
  ingestionSettings: ingestionSettingsReducer,
  namedElements: namedElementsReducer,
  classificationRules: classificationRulesReducer,
  namedElementBlockRules: namedElementBlockRuleReducer,
  productTeams: issueTrackingReducer,
  watchHistory: watchHistoryReducer,
  helpOverlay: helpOverlayReducer,
  userOverlay: userOverlayReducer,
  pages: pagesReducer,
  journeyMaps: journeyMapsReducer,
  heatmaps: heatmapsReducer,
  retentionMetrics: retentionMetricsReducer,
  home: homeReducer,
  library: libraryReducer,
  sessionReplay: sessionReplayReducer,
  sesionReplayFilters: sessionPlaylistCardFilterReducer,
  metricsInsights: metricsInsightsReducer,
  appBackend: appBackendReducer,
  notifications: notificationsReducer,
  spaces: spacesReducer,
  assistant: assistantReducer,
  specs: specsReducer,
  dxObjectsCache: dxObjectsCacheReducer,
  elementPropertyRules: elementPropertyRulesReducer,
  llmTool: llmToolReducer,
  capturedEvents: searchEventsReducer,
  properties: propertiesReducer,
});
